// ═══════════════════════════════════════════════════════════
// TopsSection — Reescrito para analítica real
// Muestra top programs y top podcasts desde Supabase
// ═══════════════════════════════════════════════════════════

import type { TopContentItem } from '@/types/analytics';

interface TopsSectionProps {
  topPrograms: TopContentItem[];
  topPodcasts: TopContentItem[];
}

const rankMedals: Record<number, string> = {
  1: 'bg-amber-400 text-white',
  2: 'bg-slate-300 text-white',
  3: 'bg-amber-600 text-white',
};

function TopRow({ item, rank, maxValue }: { item: TopContentItem; rank: number; maxValue: number }) {
  const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;

  return (
    <div className="group">
      <div className="flex items-center gap-3 mb-1.5">
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 ${
            rankMedals[rank] || 'bg-foreground-100 text-foreground-500'
          }`}
        >
          {rank}
        </div>

        <div className="flex-1 min-w-0">
          <span className="text-foreground-800 text-xs font-medium truncate block">
            {item.name}
          </span>
          {item.subtitle && (
            <p className="text-foreground-400 text-[10px] mt-0.5 truncate">{item.subtitle}</p>
          )}
        </div>

        <span className="text-foreground-950 text-xs font-bold shrink-0 text-right min-w-[60px]">
          {item.value.toLocaleString()} <span className="text-foreground-400 font-normal text-[10px]">{item.unit}</span>
        </span>
      </div>

      <div className="w-full h-1.5 rounded-full bg-background-100 overflow-hidden ml-[52px]">
        <div
          className="h-full rounded-full bg-primary-500 transition-all duration-700 ease-out"
          style={{ width: `${barWidth}%` }}
        ></div>
      </div>
    </div>
  );
}

function EmptySlot({ label }: { label: string }) {
  return (
    <div>
      <h4 className="text-foreground-800 text-xs font-semibold mb-3 flex items-center gap-2">
        <i className="ri-music-line text-foreground-400"></i>
        {label}
      </h4>
      <div className="text-center py-8">
        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-foreground-100 flex items-center justify-center">
          <i className="ri-information-line text-foreground-400"></i>
        </div>
        <p className="text-foreground-400 text-[11px]">Sin datos en este período</p>
      </div>
    </div>
  );
}

export default function TopsSection({ topPrograms, topPodcasts }: TopsSectionProps) {
  const programsMax = topPrograms.length > 0 ? Math.max(...topPrograms.map((i) => i.value)) : 1;
  const podcastsMax = topPodcasts.length > 0 ? Math.max(...topPodcasts.map((i) => i.value)) : 1;

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 p-5">
      <div className="flex items-center gap-2.5 mb-5">
        <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
          <i className="ri-trophy-line text-accent-500 text-sm"></i>
        </div>
        <div>
          <h3 className="text-foreground-950 font-bold text-sm">Contenido Destacado</h3>
          <p className="text-foreground-400 text-[10px]">Programas y podcasts principales</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          {topPrograms.length > 0 ? (
            <>
              <h4 className="text-foreground-800 text-xs font-semibold flex items-center gap-2">
                <i className="ri-music-line text-accent-500"></i>
                Programas
              </h4>
              {topPrograms.map((item, idx) => (
                <TopRow key={item.id} item={item} rank={idx + 1} maxValue={programsMax} />
              ))}
            </>
          ) : (
            <EmptySlot label="Programas" />
          )}
        </div>

        <div className="space-y-3">
          {topPodcasts.length > 0 ? (
            <>
              <h4 className="text-foreground-800 text-xs font-semibold flex items-center gap-2">
                <i className="ri-headphone-line text-secondary-500"></i>
                Podcasts
              </h4>
              {topPodcasts.map((item, idx) => (
                <TopRow key={item.id} item={item} rank={idx + 1} maxValue={podcastsMax} />
              ))}
            </>
          ) : (
            <EmptySlot label="Podcasts" />
          )}
        </div>
      </div>
    </div>
  );
}